/**
 * ComposeDialog
 *
 * Email composition modal supporting new, reply, replyAll, and forward modes.
 * Features: contact autocomplete, rich text editing, attachments, signatures,
 * templates, snippets, schedule send, and draft auto-save.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link,
  Quote,
  Paperclip,
  X,
  ChevronDown,
  Trash2,
  Clock,
  Send,
  MoreHorizontal,
  Loader2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContactChipInput } from './ContactChipInput';
import { SnippetExpander } from './SnippetExpander';
import { AIComposeAssistant } from './AIComposeAssistant';
import { useCompose, ComposeMode } from '../hooks/useCompose';
import { useSignatures } from '../hooks/useSignatures';
import { useSnippets } from '../hooks/useSnippets';
import { useTemplates } from '../hooks/useTemplates';
import { useEmailModules } from '../hooks/useEmailModules';
import { cn } from '@/lib/utils';
import type { EmailMessage, EmailMessageDetail, EmailSignature } from '@/lib/api';

interface ComposeDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
  mode?: ComposeMode;
  replyToMessage?: EmailMessage | EmailMessageDetail;
}

function getSchedulePresets(): { label: string; value: string }[] {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const tomorrowMorning = new Date(tomorrow);
  tomorrowMorning.setHours(9, 0, 0, 0);

  const tomorrowAfternoon = new Date(tomorrow);
  tomorrowAfternoon.setHours(14, 0, 0, 0);

  // Next Monday
  const nextMonday = new Date(now);
  nextMonday.setDate(nextMonday.getDate() + ((8 - nextMonday.getDay()) % 7 || 7));
  nextMonday.setHours(9, 0, 0, 0);

  return [
    { label: 'Tomorrow morning', value: tomorrowMorning.toISOString() },
    { label: 'Tomorrow afternoon', value: tomorrowAfternoon.toISOString() },
    { label: 'Monday morning', value: nextMonday.toISOString() },
  ];
}

export function ComposeDialog({
  open,
  onClose,
  accountId,
  mode = 'new',
  replyToMessage,
}: ComposeDialogProps) {
  const compose = useCompose(accountId);
  const { signatures, defaultSignature } = useSignatures(accountId);
  const { snippets } = useSnippets(accountId);
  const { templates } = useTemplates(accountId);
  const { isModuleEnabled } = useEmailModules(accountId);
  const aiComposeEnabled = isModuleEnabled('ai_compose');

  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [snippetWord, setSnippetWord] = useState('');
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize compose mode on open
  useEffect(() => {
    if (!open) return;
    compose.reset();

    if (replyToMessage) {
      if (mode === 'forward') {
        compose.initForward(replyToMessage);
      } else {
        compose.initReply(replyToMessage, mode === 'replyAll');
      }
    }

    // Set default signature
    if (defaultSignature) {
      compose.setSignature(defaultSignature.bodyHtml);
    }
  }, [open, mode, replyToMessage?.id]); // intentionally excludes compose/defaultSignature to avoid loops

  const handleEditorInput = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    compose.updateState({ bodyHtml: html });

    // Check for snippet trigger
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const text = range.startContainer.textContent || '';
      const offset = range.startOffset;
      // Find current word
      const before = text.substring(0, offset);
      const wordMatch = before.match(/\/\S*$/);
      setSnippetWord(wordMatch ? wordMatch[0] : '');
    }
  }, [compose]);

  const execCommand = useCallback((cmd: string, value?: string) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  }, []);

  const handleSnippetExpand = useCallback(
    (snippet: { trigger: string; body: string }) => {
      if (!editorRef.current) return;
      // Replace the trigger text with snippet body
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const text = range.startContainer.textContent || '';
        const offset = range.startOffset;
        const before = text.substring(0, offset);
        const triggerStart = before.lastIndexOf('/');
        if (triggerStart >= 0) {
          // Replace trigger with snippet content
          const newText = text.substring(0, triggerStart) + snippet.body + text.substring(offset);
          range.startContainer.textContent = newText;
          // Update cursor
          const newRange = document.createRange();
          newRange.setStart(range.startContainer, triggerStart + snippet.body.length);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      }
      setSnippetWord('');
      compose.updateState({ bodyHtml: editorRef.current.innerHTML });
    },
    [compose],
  );

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      files.forEach((f) => compose.addAttachment(f));
    },
    [compose],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      files.forEach((f) => compose.addAttachment(f));
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [compose],
  );

  const handleSignatureChange = useCallback(
    (sigId: string) => {
      const sig = signatures.find((s: EmailSignature) => s.id === sigId);
      if (sig) compose.setSignature(sig.bodyHtml);
    },
    [signatures, compose],
  );

  const handleClose = useCallback(() => {
    if (compose.bodyHtml.trim() || compose.subject.trim()) {
      compose.saveDraft();
    }
    compose.reset();
    onClose();
  }, [compose, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base">
              {mode === 'reply'
                ? 'Reply'
                : mode === 'replyAll'
                  ? 'Reply All'
                  : mode === 'forward'
                    ? 'Forward'
                    : 'New Message'}
            </DialogTitle>
            {/* Dialog's built-in X button handles close */}
          </div>
          {/* Internal-only delivery banner. The email arche routes messages
              between Fias accounts; addresses outside the platform are
              silently dropped on send. Surfaces the limit at compose time so
              users don't think they're emailing the outside world. */}
          <div className="mt-2 text-xs text-muted-foreground bg-muted/40 border border-border rounded px-2 py-1.5">
            Messages send only to other Fias email accounts. External addresses are ignored.
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Recipients */}
          <div className="px-4 pt-2 space-y-1">
            <ContactChipInput
              label="To"
              addresses={compose.to}
              onChange={(to) => compose.updateState({ to })}
              accountId={accountId}
            />
            {!showCc && !showBcc && (
              <div className="flex gap-2 ml-10">
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCc(true)}
                >
                  Cc
                </button>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowBcc(true)}
                >
                  Bcc
                </button>
              </div>
            )}
            {showCc && (
              <ContactChipInput
                label="Cc"
                addresses={compose.cc}
                onChange={(cc) => compose.updateState({ cc })}
                accountId={accountId}
              />
            )}
            {showBcc && (
              <ContactChipInput
                label="Bcc"
                addresses={compose.bcc}
                onChange={(bcc) => compose.updateState({ bcc })}
                accountId={accountId}
              />
            )}
          </div>

          {/* Subject */}
          <div className="px-4 py-1">
            <Input
              value={compose.subject}
              onChange={(e) => compose.updateState({ subject: e.target.value })}
              placeholder="Subject"
              className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 text-sm"
            />
          </div>

          {/* AI Compose Assistant */}
          {aiComposeEnabled && (
            <div className="px-4 py-2 border-b border-border">
              <AIComposeAssistant
                accountId={accountId}
                replyToMessageId={compose.replyToMessageId}
                bodyHtml={compose.bodyHtml}
                subject={compose.subject}
                onSetBody={(html) => compose.updateState({ bodyHtml: html })}
                onSetSubject={(subj) => compose.updateState({ subject: subj })}
              />
            </div>
          )}

          {/* Formatting toolbar */}
          <div className="flex items-center gap-0.5 px-4 py-1 border-b border-border">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => execCommand('bold')}
              title="Bold"
            >
              <Bold className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => execCommand('italic')}
              title="Italic"
            >
              <Italic className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => execCommand('underline')}
              title="Underline"
            >
              <Underline className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => execCommand('strikeThrough')}
              title="Strikethrough"
            >
              <Strikethrough className="h-3.5 w-3.5" />
            </Button>
            <div className="w-px h-4 bg-border mx-1" />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => execCommand('insertUnorderedList')}
              title="Bullet list"
            >
              <List className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => execCommand('insertOrderedList')}
              title="Numbered list"
            >
              <ListOrdered className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => {
                const url = prompt('Enter URL:');
                if (url) execCommand('createLink', url);
              }}
              title="Insert link"
            >
              <Link className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => execCommand('formatBlock', 'blockquote')}
              title="Quote"
            >
              <Quote className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Editor */}
          <div className="relative">
            <div
              ref={editorRef}
              contentEditable
              className="min-h-[200px] max-h-[300px] overflow-y-auto px-4 py-3 text-sm outline-none"
              onInput={handleEditorInput}
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              suppressContentEditableWarning
            />
            <SnippetExpander
              currentWord={snippetWord}
              snippets={snippets}
              onExpand={handleSnippetExpand}
              onDismiss={() => setSnippetWord('')}
            />
          </div>

          {/* Quoted content (read-only) */}
          {compose.quotedHtml && (
            <div
              className="px-4 py-2 text-sm text-muted-foreground border-t border-border"
              dangerouslySetInnerHTML={{ __html: compose.quotedHtml }}
            />
          )}

          {/* Signature preview */}
          {compose.signatureHtml && (
            <div className="px-4 py-2 border-t border-border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Signature</span>
                {signatures.length > 1 && (
                  <Select onValueChange={handleSignatureChange}>
                    <SelectTrigger className="h-6 w-auto text-xs border-0 p-0">
                      <SelectValue placeholder="Change" />
                    </SelectTrigger>
                    <SelectContent>
                      {signatures.map((s: EmailSignature) => (
                        <SelectItem key={s.id} value={s.id} className="text-xs">
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div
                className="text-xs text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: compose.signatureHtml }}
              />
            </div>
          )}

          {/* Attachments */}
          {compose.attachments.length > 0 && (
            <div className="px-4 py-2 border-t border-border">
              <div className="flex flex-wrap gap-2">
                {compose.attachments.map((att) => (
                  <div
                    key={att.id}
                    className={cn(
                      'flex items-center gap-1.5 bg-muted rounded-md px-2 py-1 text-xs',
                      att.uploading && 'opacity-60',
                    )}
                  >
                    <Paperclip className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate max-w-[120px]">{att.filename}</span>
                    <span className="text-muted-foreground">
                      ({Math.round(att.sizeBytes / 1024)}KB)
                    </span>
                    {att.uploading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <button
                        onClick={() => compose.removeAttachment(att.id)}
                        className="hover:text-destructive"
                        aria-label="Remove attachment"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-border flex-shrink-0">
          <div className="flex items-center gap-1">
            {/* Send */}
            <Button size="sm" onClick={compose.send} disabled={compose.sending}>
              {compose.sending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              Send
            </Button>

            {/* Schedule send */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="px-1.5">
                  <Clock className="h-4 w-4" />
                  <ChevronDown className="h-3 w-3 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {getSchedulePresets().map((preset) => (
                  <DropdownMenuItem
                    key={preset.value}
                    onClick={() => compose.scheduleSend(preset.value)}
                  >
                    {preset.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-1">
            {/* Attachments */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              title="Attach files"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />

            {/* More menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={compose.saveDraft}>Save draft</DropdownMenuItem>
                <DropdownMenuSeparator />
                {templates.length > 0 && (
                  <>
                    {templates.map((t) => (
                      <DropdownMenuItem
                        key={t.id}
                        onClick={() => compose.insertTemplate(t.subject || '', t.bodyHtml)}
                      >
                        Template: {t.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Discard */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                compose.reset();
                onClose();
              }}
              title="Discard"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
